<template>
  <!-- prettier-ignore -->
  <div class="TabsContainer">
    <ul class="tabs-header">
      <li
        v-for="(_, category) in $props.productsTree"
        :key="category"
        :class="{ active: category === activeCategory }"
        @click="setActiveCategory(category)"
        v-html="$t(`content.categories.${category}.title`)"
      />
    </ul>
    <div
      class="tabs-content"
      :class="{ 'with-person-link': itemData.isPersonDataEdited }"
    >
      <product-detail
        v-for="product in products"
        :key="product.id"
        class="product"
        :basket="itemData.basket"
        :labels="$t(`content.products.${product.productId}`)"
        :package-id="product.packageId"
        :product="product"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'

import usePartnerWidgetBundleLoader from '@/hooks/usePartnerWidgetBundleLoader'

import ProductDetail from '@/modules/PartnerWidget/components/product/ProductDetail'

// HOOKS
const { bundleConfig } = usePartnerWidgetBundleLoader()

// INIT
const { itemData } = inject('detailProps')
const emit = defineEmits(['opened'])
const props = defineProps({
  productsTree: {
    type: Object,
    default: () => ({}),
  },
})

// DATA
const activeCategory = ref(null)

// COMPUTED
const products = computed(() => {
  if (!activeCategory.value) return []
  return props.productsTree[activeCategory.value]
})

// METHODS
function setActiveCategory(category, emitOpened = true) {
  activeCategory.value = category
  if (emitOpened) {
    emit('opened', category)
  }
}

// LIFECYCLE HOOKS
onMounted(() => {
  const emitOpen = !(bundleConfig.skipPackagesOverview && bundleConfig.skipEntryPage)
  setActiveCategory(Object.keys(props.productsTree)[0], emitOpen)
})
</script>

<style scoped>
.TabsContainer {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;

  .tabs-header {
    display: flex;
    margin: 0;
    padding: 0;
    position: relative;
    top: 1px;

    li {
      list-style-type: none;
      padding: 10px;
      cursor: pointer;

      &.active {
        cursor: inherit;
        background-color: var(--c-primary-neutral-3);
        color: var(--c-primary-neutral-1);
        font-weight: 700;

        border: 1px solid var(--c-secondary-neutral-3);
        border-bottom: none;
      }
    }
  }
  .tabs-content {
    flex: 1 0 auto;
    overflow: auto;
    padding: 0 20px;
    max-height: 340px;
    border: 1px solid var(--c-secondary-neutral-3);

    &.with-person-link {
      max-height: 323px;
    }

    &::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.3);
      background-color: var(--c-primary-neutral-2);
    }

    &::-webkit-scrollbar {
      width: 5px;
      background-color: var(--c-primary-neutral-2);
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--c-primary-color-3);
    }

    .product:not(:last-of-type) {
      border-bottom: 1px solid var(--c-secondary-neutral-3);
    }
  }
}
</style>
<style name="short" scoped>
@media screen and (max-height: 596px) {
  .TabsContainer {
    .tabs-content {
      max-height: none;

      &.with-person-link {
        max-height: none;
      }
    }
  }
}
</style>
